import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Tag, Progress, Typography, Button, Alert, Spin, Space, Descriptions, Statistic } from 'antd';
import { ReloadOutlined, CloudServerOutlined, DollarOutlined, RobotOutlined } from '@ant-design/icons';

const { Text } = Typography;

// ══════════════════════════════════════════════
//  Mock 数据 — 套餐信息
//  TODO: 联调时替换为套餐接口 + PaaS 设备影子 API
// ══════════════════════════════════════════════

const MOCK_PACKAGES = {
    updateTime: '2026-06-22 15:30:12',
    isOnline: false,
    lastReportTime: '2026-06-22 14:20:05',

    packages: [
        { pkgId: 'PKG2024011', pkgName: 'Overseas-Pro 超清云存', serviceDuration: '12个月',
            activationTime: '2026-06-01 08:00:00', expirationTime: '2027-06-01 08:00:00',
            remainingDays: 344, status: 'active',
            cloudRecord: { recordType: 1, loopDays: 30, streamId: 0 } },
        { pkgId: 'PKG2024020', pkgName: '全天云存-标清版', serviceDuration: '3个月',
            activationTime: '2027-06-01 08:00:00', expirationTime: '2027-09-01 08:00:00',
            remainingDays: null, status: 'pending',
            cloudRecord: { recordType: 1, loopDays: 7, streamId: 1 } },
    ],

    fourGFlow: { hasModule: true, hasPackage: true, usedFlow: 4.0, totalFlow: 10.0, workSimSlotId: '0' },

    // Agent 套餐
    agent: {
        hasAgent: true,
        agentName: '家庭安防智能看护',
        agentId: 'test-agent-001',
        status: 'active',           // active / paused / inactive
        dailyCapture: 156,
        dailyLimit: 288,
        lastRecognition: '2026-07-20 14:32:01',
        todayTokens: 1248,
        activationTime: '2026-07-01 08:00:00',
        expirationTime: '2027-06-30 08:00:00',
        remainingDays: 344,
    },
};

// ══════════════════════════════════════════════
//  主组件：DevicePackageTab
// ══════════════════════════════════════════════

const DevicePackageTab = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [data, setData] = useState(null);

    const fetchData = () => {
        setLoading(true); setError(false);
        setTimeout(() => { setData(MOCK_PACKAGES); setLoading(false); }, 600);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchData(); }, []);

    if (loading) return <div style={{ padding: '24px 0' }}><Spin tip="正在加载套餐数据..."><div style={{ minHeight: 200 }} /></Spin></div>;
    if (error || !data) return <div style={{ padding: '64px 0', textAlign: 'center' }}>
        <div style={{ color: '#ff4d4f', fontSize: 48, marginBottom: 16 }}>!</div>
        <Text type="danger" style={{ display: 'block', marginBottom: 16 }}>数据加载失败，请稍后重试</Text>
        <Button icon={<ReloadOutlined />} onClick={fetchData}>重新加载</Button>
    </div>;

    const visiblePackages = data.packages.filter(p => p.status === 'active' || p.status === 'pending');
    const show4G = data.fourGFlow.hasModule;
    const showAgent = data.agent?.hasAgent;

    return (
        <div style={{ padding: '24px 0' }}>
            <TopBar data={data} onRetry={fetchData} />

            <SectionTitle icon={<CloudServerOutlined />} title="套餐服务" />
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Row gutter={[16, 16]}>
                        {visiblePackages.map((pkg) => (
                            <Col span={show4G || showAgent ? 12 : 24} key={pkg.pkgId}><PkgCard pkg={pkg} /></Col>
                        ))}
                        {show4G && <Col span={12}><FourGFlowCard data={data.fourGFlow} /></Col>}
                        {showAgent && <Col span={12}><AgentCard data={data.agent} /></Col>}
                    </Row>
                </Col>
            </Row>
        </div>
    );
};

// ══════════════════════════════════════════════
//  通用
// ══════════════════════════════════════════════

const TopBar = ({ data, onRetry }) => (
    <div style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle"><Col><Text type="secondary" style={{ fontSize: 13 }}>数据更新时间：{data.updateTime}</Text></Col><Col><Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>刷新</Button></Col></Row>
        {data.isOnline === false && <Alert message={`设备当前离线，以下数据为最近一次上报（${data.lastReportTime}）`} type="warning" showIcon style={{ marginTop: 12, borderRadius: 8 }} />}
    </div>
);

const SectionTitle = ({ icon, title }) => (
    <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ fontSize: 16, color: '#1890ff' }}>{icon}</span><Text strong style={{ fontSize: 15 }}>{title}</Text></div>
);

// ══════════════════════════════════════════════
//  套餐卡片
// ══════════════════════════════════════════════

const PkgCard = ({ pkg }) => {
    const isActive = pkg.status === 'active';
    const isPending = pkg.status === 'pending';
    const { cloudRecord } = pkg;
    return (
        <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '20px 24px' }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col><Space size={8}><CloudServerOutlined style={{ fontSize: 16, color: '#1890ff' }} /><Text strong style={{ fontSize: 15 }}>{pkg.pkgName}</Text></Space></Col>
                <Col>{isActive && <Tag color="green" style={{ border: 'none' }}>生效中</Tag>}{isPending && <Tag color="blue" style={{ border: 'none' }}>待生效</Tag>}</Col>
            </Row>
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}><div style={{ background: '#f5f5f5', borderRadius: 8, padding: '12px', textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>服务时长</Text><Text strong style={{ fontSize: 16, color: '#1890ff' }}>{pkg.serviceDuration}</Text></div></Col>
                <Col span={8}><div style={{ background: '#f5f5f5', borderRadius: 8, padding: '12px', textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>开通时间</Text><Text style={{ fontSize: 13 }}>{isPending ? '--' : pkg.activationTime}</Text></div></Col>
                <Col span={8}><div style={{ background: isPending ? '#e6f7ff' : '#f6ffed', borderRadius: 8, padding: '12px', textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>过期时间</Text><Text style={{ fontSize: 13 }}>{pkg.expirationTime}</Text><div style={{ marginTop: 4 }}>{isActive && pkg.remainingDays > 0 && <Tag color="green" style={{ border: 'none', fontSize: 11 }}>剩余 {pkg.remainingDays} 天</Tag>}{isPending && <Tag color="blue" style={{ border: 'none', fontSize: 11 }}>尚未生效</Tag>}</div></div></Col>
            </Row>
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 14 }}>
                <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>{isPending ? '待生效配置' : '当前生效配置'}</Text>
                <Row gutter={24}>
                    <Col span={8}><Statistic title="录制模式" value={cloudRecord.recordType === 1 ? '全天录制' : '事件录制'} valueStyle={{ fontSize: 16 }} /></Col>
                    <Col span={8}><Statistic title="存储天数" value={`${cloudRecord.loopDays} 天`} valueStyle={{ fontSize: 16 }} /></Col>
                    <Col span={8}><Statistic title="录制码流" value={cloudRecord.streamId === 0 ? '主码流' : '次码流'} valueStyle={{ fontSize: 16 }} /></Col>
                </Row>
            </div>
        </Card>
    );
};

// ══════════════════════════════════════════════
//  Agent 套餐卡片
// ══════════════════════════════════════════════

const AgentCard = ({ data }) => {
    const pct = data.dailyLimit > 0 ? Math.round((data.dailyCapture / data.dailyLimit) * 100) : 0;
    return (
        <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '100%' }} bodyStyle={{ padding: '20px 24px' }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col><Space size={8}><RobotOutlined style={{ fontSize: 16, color: '#722ed1' }} /><Text strong style={{ fontSize: 15 }}>{data.agentName}</Text></Space></Col>
                <Col><Tag color="green" style={{ border: 'none' }}>运行中</Tag></Col>
            </Row>
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}><div style={{ background: '#f5f5f5', borderRadius: 8, padding: '12px', textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>今日抓图</Text><Text strong style={{ fontSize: 16, color: '#722ed1' }}>{data.dailyCapture}<Text type="secondary" style={{ fontSize: 13 }}>/{data.dailyLimit}</Text></Text></div></Col>
                <Col span={8}><div style={{ background: '#f5f5f5', borderRadius: 8, padding: '12px', textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>最近识别</Text><Text style={{ fontSize: 12 }}>{data.lastRecognition?.split(' ')[1]}</Text></div></Col>
                <Col span={8}><div style={{ background: '#f6ffed', borderRadius: 8, padding: '12px', textAlign: 'center' }}><Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Token消耗</Text><Text strong style={{ fontSize: 14 }}>{data.todayTokens?.toLocaleString()}</Text></div></Col>
            </Row>
            <Progress percent={pct} size="small" strokeColor={pct >= 80 ? '#fa8c16' : '#722ed1'} style={{ marginBottom: 12 }} />
            <Descriptions column={2} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}>
                <Descriptions.Item label="开通时间">{data.activationTime}</Descriptions.Item>
                <Descriptions.Item label="过期时间">{data.expirationTime}</Descriptions.Item>
                <Descriptions.Item label="剩余天数"><Tag color="green" style={{ border: 'none' }}>{data.remainingDays} 天</Tag></Descriptions.Item>
            </Descriptions>
        </Card>
    );
};

const FourGFlowCard = ({ data }) => {
    const hasPkg = data?.hasPackage;
    const pct = hasPkg && data.totalFlow > 0 ? Math.round((data.usedFlow / data.totalFlow) * 100) : 0;
    return (
        <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '100%' }} bodyStyle={{ padding: '20px 24px' }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}><Col><Space size={8}><DollarOutlined style={{ fontSize: 16, color: '#fa8c16' }} /><Text strong style={{ fontSize: 15 }}>4G 流量套餐</Text></Space></Col><Col>{hasPkg ? <Tag color="green" style={{ border: 'none' }}>生效中</Tag> : <Tag color="default" style={{ border: 'none' }}>未开通</Tag>}</Col></Row>
            {hasPkg ? <Row gutter={16} align="middle"><Col span={10}><Progress type="dashboard" percent={pct} size={90} strokeColor={pct >= 80 ? '#ff4d4f' : '#1890ff'} format={() => `${pct}%`} /></Col><Col span={14}><Descriptions column={1} size="small" colon={false} labelStyle={{ color: '#8c8c8c', fontSize: 12 }}><Descriptions.Item label="已用流量"><Text strong style={{ fontSize: 15 }}>{data.usedFlow.toFixed(1)} GB</Text></Descriptions.Item><Descriptions.Item label="总流量"><Text>{data.totalFlow.toFixed(0)} GB</Text></Descriptions.Item><Descriptions.Item label="生效卡槽"><Tag color="blue" style={{ border: 'none' }}>卡槽 {data.workSimSlotId}</Tag></Descriptions.Item></Descriptions></Col></Row> : <div style={{ textAlign: 'center', padding: '24px 0' }}><Text type="secondary">未开通</Text><br /><Text type="secondary" style={{ fontSize: 12 }}>暂无有效套餐信息</Text></div>}
        </Card>
    );
};

export default DevicePackageTab;
